import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { RouteLocationNormalized } from "vue-router";

export type EntryAttribution = {
  buildingId: string | null;
  entryPoint: string;
  entrySessionId: string;
  capturedAt: number;
};

export type EntryRouteTarget = "assistant";

const VALID_ROUTE_TARGETS: EntryRouteTarget[] = ["assistant"];

export const useEntryAttributionStore = defineStore("entryAttribution", () => {
  const attribution = ref<EntryAttribution | null>(null);
  const sentAt = ref<number | null>(null);
  const selectedBuildingId = ref<string | null>(null);
  const routeTo = ref<EntryRouteTarget | null>(null);

  const hasPendingAttribution = computed<boolean>(() => {
    return !!attribution.value && !sentAt.value;
  });

  function normalizeBuildingId(value: unknown): string | null {
    return typeof value === "string" && value.trim().length
      ? value.trim().toUpperCase()
      : null;
  }

  function normalizeEntryPoint(value: unknown): string | null {
    return typeof value === "string" && value.trim().length
      ? value.trim()
      : null;
  }

  function normalizeRouteTarget(value: unknown): EntryRouteTarget | null {
    return typeof value === "string" && VALID_ROUTE_TARGETS.includes(value as EntryRouteTarget)
      ? value as EntryRouteTarget
      : null;
  }

  function captureFromRoute(route: RouteLocationNormalized): void {
    const buildingId = normalizeBuildingId(route.query.building_id);
    const entryPoint = normalizeEntryPoint(route.query.entry_point);
    const target = normalizeRouteTarget(route.query.route_to);

    if (buildingId) {
      selectedBuildingId.value = buildingId;
    }

    if (target) {
      routeTo.value = target;
    }

    if (!entryPoint) return;
    if (
      sentAt.value
      && attribution.value?.buildingId === buildingId
      && attribution.value?.entryPoint === entryPoint
    ) return;

    attribution.value = {
      buildingId,
      entryPoint,
      entrySessionId: crypto.randomUUID(),
      capturedAt: Date.now()
    };
    sentAt.value = null;
  }

  function setSelectedBuildingId(buildingId: string | null): void {
    selectedBuildingId.value = buildingId?.trim().toUpperCase() || null;
  }

  function consumeRouteTo(): EntryRouteTarget | null {
    const target = routeTo.value;
    routeTo.value = null;
    return target;
  }

  function peekPendingAttribution(): EntryAttribution | null {
    return hasPendingAttribution.value ? attribution.value : null;
  }

  function markSent(): void {
    if (!attribution.value) return;
    sentAt.value = Date.now();
  }

  function clear(): void {
    attribution.value = null;
    sentAt.value = null;
    routeTo.value = null;
  }

  function clearSelectedBuildingId(): void {
    selectedBuildingId.value = null;
  }

  return {
    attribution,
    sentAt,
    selectedBuildingId,
    routeTo,
    hasPendingAttribution,
    captureFromRoute,
    setSelectedBuildingId,
    consumeRouteTo,
    peekPendingAttribution,
    markSent,
    clear,
    clearSelectedBuildingId
  };
}, {
  persist: {
    key: "entry-attribution",
    paths: ["attribution", "sentAt", "selectedBuildingId"]
  }
});
